import { DateTime } from "luxon";
import { OUTSIDE_RANGE, RangeRole } from "../calendar/calendar-styles";
import { CaptureBucket } from "../capture-date-stats";

/**
 * A row of the capture timeline list: either a month holding captures, which summarizes
 * the rows of its days that follow, or a single day holding captures.
 */
export type TimelineRow = {
  /** The day of the row, or the first instant of the month for a month row */
  date: DateTime;
  /** How many captures the row holds; a month row sums those of its days */
  count: number;
  /** True for a month row, false for a day row */
  isMonth: boolean;
};

/**
 * @returns The timeline's rows, newest first, each month's row ahead of its days
 * @param days Days holding captures, oldest first
 */
export function convertCaptureBucketsToTimelineRows(
  days: CaptureBucket[],
): TimelineRow[] {
  const rows: TimelineRow[] = [];
  let month: TimelineRow | undefined;
  for (const { start, count } of [...days].reverse()) {
    const date = DateTime.fromJSDate(start);
    if (!month?.date.hasSame(date, "month")) {
      month = { date: date.startOf("month"), count: 0, isMonth: true };
      rows.push(month);
    }
    month.count += count;
    rows.push({ date, count, isMonth: false });
  }
  return rows;
}

/**
 * The range reads as one block down the timeline, its ends being the top and bottom row
 * inside it. A month row counts as inside when the range runs on past the month, or ends
 * with the month and holds all of it.
 *
 * @returns How each row relates to the active range
 * @param rows The timeline's rows, in order
 * @param start Inclusive start of the active range
 * @param end Inclusive end of the active range
 */
export function computeTimelineRowRoles(
  rows: TimelineRow[],
  start?: Date,
  end?: Date,
): RangeRole[] {
  if (!start || !end) return rows.map(() => OUTSIDE_RANGE);

  const isRowInsideMap = rows.map(({ date, isMonth }) => {
    const from = date.toJSDate();
    const to = date.endOf(isMonth ? "month" : "day").toJSDate();
    const overlaps = from <= end && to >= start;
    if (!isMonth) return overlaps;
    const runsPastMonth = end > to;
    const coversMonth = start <= from && end >= to;
    return overlaps && (runsPastMonth || coversMonth);
  });

  const top = isRowInsideMap.indexOf(true);
  const bottom = isRowInsideMap.lastIndexOf(true);
  // Rows run newest first, so the range starts at the bottom row
  return isRowInsideMap.map((isSelected, index) =>
    isSelected
      ? {
          isSelected,
          isRangeStart: index === bottom,
          isRangeEnd: index === top,
        }
      : OUTSIDE_RANGE,
  );
}
